import React, { useMemo } from "react";
import { Paper, Stack, Typography, Box, Button } from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AddIcon from "@mui/icons-material/Add";

import InfoButton from "../Clinical/InfoButton";
import TaskStats from "../Clinical/TaskStats";
import TaskList from "../Clinical/TaskList";

/**
 * Panel CM Tasks — wrapper que reutiliza componentes de Clinical tasks
 * con un stat extra de "This Week" (tareas creadas en los últimos 7 días).
 *
 * @param {{
 *   tasks: Array,
 *   stats: { dueToday: number, overdue: number, completedToday: number },
 *   loading: boolean,
 *   onComplete: (task) => void,
 *   onCancel: (task) => void,
 *   onDelete: (task) => void,
 *   onEdit: (task) => void,
 *   onNewTask: () => void,
 *   helpContent: object
 * }} props
 */
export default function PanelCMTasks({
  tasks,
  stats,
  loading,
  onComplete,
  onCancel,
  onDelete,
  onEdit,
  onNewTask,
  helpContent,
}) {
  // Compute how many tasks were created in the last 7 days
  const thisWeekCount = useMemo(() => {
    if (!tasks || tasks.length === 0) return 0;
    const now = new Date();
    const sevenDaysAgo = new Date(now);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    return tasks.filter((t) => {
      if (!t.createdAt) return false;
      const created = new Date(t.createdAt);
      return created >= sevenDaysAgo;
    }).length;
  }, [tasks]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack spacing={2}>
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 28,
                height: 28,
                borderRadius: 1.5,
                bgcolor: "#EFF6FF",
                color: "#2563EB",
              }}
            >
              <AssignmentIcon sx={{ fontSize: 16 }} />
            </Box>
            <Typography variant="subtitle2" fontWeight={700}>
              My Tasks
            </Typography>
            <InfoButton helpContent={helpContent} />
          </Stack>
          <Button
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 14 }} />}
            onClick={onNewTask}
            sx={{
              textTransform: "none",
              fontSize: "0.72rem",
              borderRadius: 2,
            }}
          >
            New Task
          </Button>
        </Stack>

        {/* Task Stats */}
        <TaskStats stats={stats} />

        {/* This Week stat */}
        <Box
          sx={{
            px: 1.5,
            py: 0.75,
            borderRadius: 1.5,
            bgcolor: "#F0FDF4",
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            alignSelf: "flex-start",
          }}
        >
          <Typography
            variant="subtitle2"
            fontWeight={800}
            sx={{ color: "#16A34A", lineHeight: 1.1 }}
          >
            {thisWeekCount}
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontSize: "0.65rem", color: "#16A34A", fontWeight: 600 }}
          >
            This Week
          </Typography>
        </Box>

        {/* Task List */}
        <TaskList
          tasks={tasks}
          loading={loading}
          onComplete={onComplete}
          onCancel={onCancel}
          onDelete={onDelete}
          onEdit={onEdit}
        />
      </Stack>
    </Paper>
  );
}
